<template>
  <div class="chart-container">
    <Line :data="chartData" :options="chartOptions" ref="chart"/>
  </div>
</template>

<script>
import {Line} from 'vue-chartjs';
import annotationPlugin from "chartjs-plugin-annotation";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  TimeScale,
  LinearScale,
  PointElement,
  CategoryScale,
  Filler
} from 'chart.js';
import constants from "@/libs/Constants";

ChartJS.register(Title, Tooltip, Legend, LineElement, TimeScale, LinearScale, PointElement, CategoryScale, Filler, annotationPlugin);

export default {
  components: {Line},
  props: {
    propLabels: Object,
    propDatasets: Object,
    propTitle: String,

    //Axes
    propXAxisLabel: String,
    propYAxisLabel: String,
    propAnnotations: {type: Object, default: null},
    propDownloadFileName: {type: String, default: null},
    propDownloadToggle: {type: Boolean, default: false},

    //Round values in tooltip
    propTooltipValueRound: {type: Number, default: 1},

  },
  watch: {
    propDownloadToggle(newValue, oldValue) {
      let canvas = this.$refs.chart.chart.canvas;
      const ctx = canvas.getContext('2d');

      // Create a temporary white background
      const prevComposite = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = prevComposite;

      // Generate image and download
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = this.propDownloadFileName !== null ? `${this.propDownloadFileName}.png` : 'plot.png';
      link.click();
    }
  },
  computed: {
    chartData() {
      return {
        labels: this.propLabels || [],
        datasets: this.propDatasets || []
      };
    },
    chartOptions() {
      let tooltipValueRound = this.propTooltipValueRound;

      let options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true, //TODO: is it necessary?
            title: {
              display: this.propYAxisLabel !== null && this.propYAxisLabel !== '',
              text: this.propYAxisLabel //Unit of measurement
            },
            grid: {
              color: (context) => context.tick.value === 0 ? '#292929' : '#e5e5e5', // Zero line
              lineWidth: (context) => context.tick.value === 0 ? 2 : 1,
            },
            border: {
              dash: (context) => context.tick.value === 0 ? [4, 4] : [], // Zero line
            },
          },
          x: {
            title: {
              display: this.propXAxisLabel !== null && this.propXAxisLabel !== '',
              text: this.propXAxisLabel //Unit of measurement
            }
          }
        },
        plugins: {
          title: {
            display: this.propTitle !== null && this.propTitle !== '' && this.propTitle !== undefined,
            text: this.propTitle !== null && this.propTitle !== '' && this.propTitle !== undefined ? this.propTitle : ''
          },
          legend: {
            labels: {
              usePointStyle: true,
              filter: function (item, chart) {
                // hide empty label dataset from legend
                return item.text !== constants.LABELS.NO_LABEL;
              }
            },
          },
          tooltip: {
            filter: (tooltipItem) => {
              // hide tooltip for datasets with specific (empty) label
              return tooltipItem.dataset.label !== constants.LABELS.NO_LABEL;
            },
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';

                if (label)
                  label = `${label}: `;

                if (tooltipValueRound !== null && tooltipValueRound !== undefined) {
                  if (context.parsed.y !== null) {
                    let valueRound = parseFloat(context.parsed.y);
                    if (valueRound !== null && valueRound !== undefined)
                      valueRound = valueRound.toFixed(tooltipValueRound);
                    else
                      valueRound = context.parsed.y;

                    label = `${label}${valueRound}`;
                  }
                } else {
                  label = `${label}${context.parsed.y}`;
                }

                return label;
              }
            }
          }
        }
      }

      if (this.propAnnotations !== null && this.propAnnotations !== undefined)
        options.plugins.annotation = {annotations: this.propAnnotations};

      return options;
    }
  }
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  min-height: 350px;
}
</style>
